import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { Close, ZoomOutMap } from '@mui/icons-material';
import { COLORS, glassCardSx, sectionTitleSx } from './clientPortalTheme';

export const CHART_HEIGHT_DEFAULT = 400;
export const CHART_HEIGHT_EXPANDED = 580;

const ClientPortalExpandableChart = ({
  title,
  description,
  footer,
  paperSx,
  children,
  chartHeight = CHART_HEIGHT_DEFAULT,
  expandedHeight = CHART_HEIGHT_EXPANDED
}) => {
  const [open, setOpen] = useState(false);

  const renderChart = (height, expanded) => (
    typeof children === 'function' ? children({ height, expanded }) : children
  );

  const header = (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
      <Box flex={1}>
        <Typography variant="subtitle1" fontWeight={800} gutterBottom sx={sectionTitleSx}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 2 }}>
            {description}
          </Typography>
        )}
      </Box>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{
          color: COLORS.textMuted,
          bgcolor: COLORS.glassLightFlat,
          border: `1.5px solid ${COLORS.glassBorder}`,
          flexShrink: 0,
          '&:hover': { bgcolor: COLORS.glassHoverFlat, color: COLORS.indigo }
        }}
        aria-label={`Expand ${title} chart`}
      >
        <ZoomOutMap fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Paper
        sx={{
          ...paperSx,
          cursor: 'pointer',
          position: 'relative',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': {
            boxShadow: '0 12px 36px rgba(15,39,68,0.1), inset 0 1px 0 rgba(255,255,255,0.9)'
          }
        }}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-label={`Open detailed view for ${title}`}
      >
        {header}
        {renderChart(chartHeight, false)}
        {footer}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'right',
            color: COLORS.textMuted,
            mt: 1,
            fontStyle: 'italic'
          }}
        >
          Click chart for detailed view
        </Typography>
      </Paper>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            ...glassCardSx,
            borderRadius: 4,
            maxHeight: '92vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: 1,
            color: COLORS.text
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>{title}</Typography>
            {description && (
              <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5 }}>
                {description}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            sx={{ color: COLORS.textMuted }}
            aria-label="Close detailed chart view"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          {renderChart(expandedHeight, true)}
          {footer}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientPortalExpandableChart;
